import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { api } from '../api/client'
import type { Asset, Contract, ContractDocument, ContractEvent, ContractItem, CoveredAsset, EagleSLA } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from '../components/common'
import {
  BadgeStatoContratto, CellaScadenza, etichettaCampoContratto, etichettaEventoContratto, etichettaPeriodicita,
  etichettaStatoContratto, etichettaTipoContratto, formattaImporto,
} from '../components/contratti'
import { EstensioniSupporto } from '../components/EstensioniSupporto'
import { ModuloContratto, valoriDa } from '../components/ModuloContratto'
import { DataTable, type Column } from '../components/DataTable'
import { Scorrevole } from '../components/Scorrevole'
import { CellaTag, useTagDi } from '../components/Tag'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * La scheda di un contratto: le condizioni, i beni coperti, il supporto esteso
 * (components/EstensioniSupporto), le voci e i costi, i documenti, lo storico. Le azioni che cambiano il corso del
 * contratto (rinnovo, cessazione, duplicato) hanno i loro pulsanti e scrivono
 * lo storico: lo stato non si cambia a mano per dire «l'ho rinnovato».
 */
type Scheda = 'dati' | 'copertura' | 'supporto' | 'voci' | 'documenti' | 'storico'

export default function ContractDetail() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { principal } = useAuth()
  const puoModificare = principal?.roles.some((r) => ['tenant_admin', 'provider_admin', 'operator'].includes(r)) ?? false
  const contratto = useAsync(() => api.contract(id), [id])
  const catalog = useAsync(() => api.contractCatalog(), [])
  const anagrafiche = useAsync(() => api.masterDataTutte(), [])
  const slas = useAsync<EagleSLA[]>(() => api.eagleNotify().then((r) => r.slas).catch(() => []), [])
  const copertura = useAsync(() => api.contractAssets(id), [id])
  const storico = useAsync(() => api.contractEvents(id), [id])
  const tag = useTagDi('contract')
  const [scheda, setScheda] = useState<Scheda>('dati')
  const [modifica, setModifica] = useState(false)
  const [azione, setAzione] = useState<'rinnova' | 'cessa' | 'duplica' | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const c = contratto.data
  if (contratto.loading && !c) return <Spinner />
  if (!c) return <ErrorBanner error={contratto.error ?? new Error(t('contratto inesistente'))} />

  async function ricarica() {
    await Promise.all([contratto.reload(), storico.reload(), copertura.reload()])
  }
  async function esegui(fn: () => Promise<unknown>) {
    setBusy(true)
    setErrore(null)
    try {
      await fn()
      await ricarica()
      return true
    } catch (e) {
      setErrore(e)
      return false
    } finally {
      setBusy(false)
    }
  }
  async function salvaCampi(patch: Record<string, unknown>) {
    await api.updateContract(id, patch)
    await ricarica()
  }
  async function elimina() {
    if (!window.confirm(t('Eliminare il contratto «{ref}» con la sua copertura e il suo storico? Per un contratto finito è meglio la cessazione.', { ref: c!.reference }))) return
    if (await esegui(() => api.deleteContract(id))) navigate('/contracts')
  }

  const chiuso = c.status === 'renewed' || c.status === 'terminated'
  const coperti = copertura.data ?? []

  return (
    <>
      <header className="page__header">
        <div>
          <p className="muted" style={{ margin: 0 }}><Link to="/contracts">{t('Contratti')}</Link> / {etichettaTipoContratto(c.contract_type)}</p>
          <h1 className="page__title mono">{c.reference}{c.name && <span className="muted" style={{ fontWeight: 400 }}> · {c.name}</span>}</h1>
          <p className="page__subtitle" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <BadgeStatoContratto stato={c.effective_status} />
            <span>{c.vendor}{c.vendor_contact && <span className="muted"> · {c.vendor_contact}</span>}</span>
            {c.renewed_from_id && <Link to={`/contracts/${c.renewed_from_id}`}>{t('← contratto precedente')}</Link>}
            {tag.vede && <CellaTag tipo="contract" id={c.id} tags={tag.di(c.id)} scrive={tag.scrive} onChanged={() => void tag.reload()} />}
          </p>
        </div>
        {puoModificare && !modifica && !azione && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => { setModifica(true); setScheda('dati') }}>{t('Modifica')}</button>
            {!chiuso && c.status !== 'draft' && <button type="button" className="secondary" onClick={() => setAzione('rinnova')}>{t('Rinnova')}</button>}
            {!chiuso && <button type="button" className="secondary" onClick={() => setAzione('cessa')}>{t('Cessa')}</button>}
            <button type="button" className="secondary" onClick={() => setAzione('duplica')}>{t('Duplica')}</button>
            <button type="button" className="secondary" onClick={elimina} disabled={busy}>{t('Elimina')}</button>
          </div>
        )}
      </header>

      <ErrorBanner error={errore ?? contratto.error} />

      <div className="stats">
        <div className="stat">
          <div className="stat__value"><CellaScadenza contratto={c} /></div>
          <div className="stat__label">{t('Data che conta')}</div>
        </div>
        <div className="stat">
          <div className="stat__value">{formattaImporto(c.annual_value, c.currency)}</div>
          <div className="stat__label">{t('Valore annuo')}</div>
          {c.value !== null && <div className="stat__hint">{formattaImporto(c.value, c.currency)} · {etichettaPeriodicita(c.billing_period)}</div>}
        </div>
        <div className="stat">
          <div className="stat__value">{c.assets_covered}</div>
          <div className="stat__label">{t('Beni coperti')}</div>
          {coperti.length > 0 && <div className="stat__hint">{t('{n} validi oggi', { n: coperti.filter((a) => a.valid_today).length })}</div>}
        </div>
        <div className="stat">
          <div className="stat__value">{c.end_date ?? '—'}</div>
          <div className="stat__label">{t('Scadenza')}</div>
          {c.auto_renew && <div className="stat__hint">{t('rinnovo tacito')}{c.renewal_months ? ` · ${t('{n} mesi', { n: c.renewal_months })}` : ''}</div>}
        </div>
      </div>

      {azione === 'rinnova' && <Rinnova c={c} busy={busy} onAnnulla={() => setAzione(null)}
                                        onEsegui={async (corpo) => { setBusy(true); setErrore(null); try { const n = await api.renewContract(id, corpo); setAzione(null); navigate(`/contracts/${n.id}`) } catch (e) { setErrore(e) } finally { setBusy(false) } }} />}
      {azione === 'cessa' && <Cessa busy={busy} onAnnulla={() => setAzione(null)}
                                    onEsegui={async (corpo) => { if (await esegui(() => api.terminateContract(id, corpo))) setAzione(null) }} />}
      {azione === 'duplica' && <Duplica c={c} busy={busy} onAnnulla={() => setAzione(null)}
                                        onEsegui={async (ref) => { setBusy(true); setErrore(null); try { const n = await api.duplicateContract(id, ref); setAzione(null); navigate(`/contracts/${n.id}`) } catch (e) { setErrore(e) } finally { setBusy(false) } }} />}

      <div className="segmented" role="tablist">
        {([
          ['dati', t('Dati')], ['copertura', t('Copertura ({n})', { n: c.assets_covered })], ['supporto', t('Supporto esteso')], ['voci', t('Voci e costi')],
          ['documenti', t('Documenti ({n})', { n: c.documents.length })], ['storico', t('Storico')],
        ] as [Scheda, string][]).map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={scheda === k} className={scheda === k ? 'segmented__on' : ''}
                  onClick={() => { setScheda(k); setModifica(false) }}>{label}</button>
        ))}
      </div>

      {scheda === 'dati' && (
        <Card title={modifica ? t('Modifica del contratto') : t('Condizioni')}>
          {modifica ? (
            <ModuloContratto iniziale={valoriDa(c)} catalogo={catalog.data} anagrafiche={anagrafiche.data} slas={slas.data ?? []} nuovo={false}
                             onSalva={async (corpo) => { await salvaCampi(corpo); setModifica(false) }} onAnnulla={() => setModifica(false)} />
          ) : (
            <Dati c={c} />
          )}
        </Card>
      )}
      {scheda === 'copertura' && (
        <Copertura contratto={c} coperti={coperti} caricamento={copertura.loading && !copertura.data} puoModificare={puoModificare && !chiuso}
                   busy={busy} esegui={esegui} />
      )}
      {scheda === 'supporto' && <EstensioniSupporto contratto={c} puoModificare={puoModificare && !chiuso} />}
      {scheda === 'voci' && <Voci c={c} puoModificare={puoModificare} onSalva={(items) => salvaCampi({ items })} />}
      {scheda === 'documenti' && <Documenti c={c} puoModificare={puoModificare} onSalva={(documents) => salvaCampi({ documents })} />}
      {scheda === 'storico' && (
        <Storico eventi={storico.data ?? []} caricamento={storico.loading && !storico.data} puoModificare={puoModificare}
                 onNota={async (testo) => { await api.addContractNote(id, testo); await storico.reload() }} />
      )}
    </>
  )
}

function Dati({ c }: { c: Contract }) {
  const Voce = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div><dt>{label}</dt><dd>{children ?? <span className="text-muted">—</span>}</dd></div>
  )
  const vuoto = <span className="text-muted">—</span>
  return (
    <dl className="contratto-dati">
      <Voce label={t('Tipo')}>{etichettaTipoContratto(c.contract_type)}</Voce>
      <Voce label={t('Stato')}><BadgeStatoContratto stato={c.effective_status} />{c.status !== c.effective_status && <span className="muted"> ({t('scritto: {s}', { s: etichettaStatoContratto(c.status) })})</span>}</Voce>
      <Voce label={t('Decorrenza')}>{c.start_date ?? vuoto}</Voce>
      <Voce label={t('Scadenza')}>{c.end_date ?? vuoto}</Voce>
      <Voce label={t('Rinnovo tacito')}>{c.auto_renew ? `${t('sì')}${c.notice_days ? ` · ${t('{n} giorni di preavviso', { n: c.notice_days })}` : ''}` : t('no')}</Voce>
      <Voce label={t('Durata del rinnovo (mesi)')}>{c.renewal_months ?? vuoto}</Voce>
      <Voce label={t('Valore')}>{c.value !== null ? `${formattaImporto(c.value, c.currency)} · ${etichettaPeriodicita(c.billing_period)}` : vuoto}</Voce>
      <Voce label={t('Valore annuo')}>{formattaImporto(c.annual_value, c.currency)}</Voce>
      <Voce label={t('Termini di pagamento')}>{c.payment_terms ?? vuoto}</Voce>
      <Voce label={t('Riferimento esterno')}>{c.external_ref ? <span className="mono">{c.external_ref}</span> : vuoto}</Voce>
      <Voce label={t('Livello di servizio')}>{c.service_level ?? vuoto}</Voce>
      <Voce label={t('SLA di Eagle')}>{c.eagle_sla_name ? <Link to="/eagle/notifiche">{c.eagle_sla_name}</Link> : vuoto}</Voce>
      <Voce label={t('Centro di costo')}>{c.cost_center_name ?? vuoto}</Voce>
      <Voce label={t('Unità organizzativa')}>{c.org_unit_name ?? vuoto}</Voce>
      <Voce label={t('Sede')}>{c.location_name ?? vuoto}</Voce>
      <Voce label={t('Responsabile')}>{c.owner ? <>{c.owner}{c.owner_email && <span className="muted"> · {c.owner_email}</span>}</> : (c.owner_email ?? vuoto)}</Voce>
      <Voce label={t('Promemoria (giorni prima)')}>{c.reminder_days.join(', ')}{!c.reminder_days_custom && <span className="muted"> ({t('predefiniti')})</span>}</Voce>
      {c.terminated_at && <Voce label={t('Cessato il')}>{c.terminated_at}{c.termination_reason && <span className="muted"> · {c.termination_reason}</span>}</Voce>}
      {c.notes && <div style={{ gridColumn: '1 / -1' }}><dt>{t('Note')}</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{c.notes}</dd></div>}
    </dl>
  )
}

function Rinnova({ c, busy, onEsegui, onAnnulla }: { c: Contract; busy: boolean; onEsegui: (corpo: Record<string, unknown>) => Promise<void>; onAnnulla: () => void }) {
  const [ref, setRef] = useState('')
  const [inizio, setInizio] = useState('')
  const [fine, setFine] = useState('')
  const [valore, setValore] = useState('')
  const [note, setNote] = useState('')
  return (
    <Card title={t('Rinnova il contratto')}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('Nasce un contratto nuovo con le stesse condizioni e gli stessi beni (quelli ancora coperti); questo resta «rinnovato», collegato al nuovo. Vuoto = la scadenza si calcola dalla durata del rinnovo, il riferimento diventa «{ref}/R1».', { ref: c.reference.split('/R')[0] })}
      </p>
      <div className="eagle-report__riga">
        <label className="field"><span className="field__label">{t('Nuovo riferimento')}</span><input value={ref} onChange={(e) => setRef(e.target.value)} className="mono" placeholder={`${c.reference.split('/R')[0]}/R…`} /></label>
        <label className="field" style={{ maxWidth: 190 }}><span className="field__label">{t('Decorrenza')}</span><input type="date" value={inizio} onChange={(e) => setInizio(e.target.value)} /></label>
        <label className="field" style={{ maxWidth: 190 }}><span className="field__label">{t('Scadenza')}</span><input type="date" value={fine} onChange={(e) => setFine(e.target.value)} /></label>
        <label className="field" style={{ maxWidth: 190 }}><span className="field__label">{t('Valore')}</span><input type="number" step="0.01" min="0" value={valore} onChange={(e) => setValore(e.target.value)} placeholder={c.value === null ? '' : String(c.value)} /></label>
      </div>
      <label className="field"><span className="field__label">{t('Note')}</span><input value={note} onChange={(e) => setNote(e.target.value)} /></label>
      <div className="eagle-form__azioni">
        <button type="button" disabled={busy} onClick={() => void onEsegui({
          ...(ref.trim() ? { reference: ref.trim() } : {}), ...(inizio ? { start_date: inizio } : {}), ...(fine ? { end_date: fine } : {}),
          ...(valore ? { value: Number(valore) } : {}), ...(note.trim() ? { notes: note.trim() } : {}),
        })}>{busy ? t('Salvo…') : t('Rinnova')}</button>
        <button type="button" className="secondary" onClick={onAnnulla} disabled={busy}>{t('Annulla')}</button>
      </div>
    </Card>
  )
}

function Cessa({ busy, onEsegui, onAnnulla }: { busy: boolean; onEsegui: (corpo: { at: string | null; reason: string | null }) => Promise<void>; onAnnulla: () => void }) {
  const [quando, setQuando] = useState('')
  const [motivo, setMotivo] = useState('')
  return (
    <Card title={t('Cessa il contratto')}>
      <p className="muted" style={{ marginTop: 0 }}>{t('La copertura dei beni si chiude a quella data e resta visibile: «quel guasto di marzo era coperto?» avrà ancora una risposta.')}</p>
      <div className="eagle-report__riga">
        <label className="field" style={{ maxWidth: 190 }}><span className="field__label">{t('Cessato il')}</span><input type="date" value={quando} onChange={(e) => setQuando(e.target.value)} /></label>
        <label className="field"><span className="field__label">{t('Motivo')}</span><input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder={t('cambio fornitore, bene dismesso, recesso…')} /></label>
      </div>
      <div className="eagle-form__azioni">
        <button type="button" disabled={busy} onClick={() => void onEsegui({ at: quando || null, reason: motivo.trim() || null })}>{busy ? t('Salvo…') : t('Cessa')}</button>
        <button type="button" className="secondary" onClick={onAnnulla} disabled={busy}>{t('Annulla')}</button>
      </div>
    </Card>
  )
}

function Duplica({ c, busy, onEsegui, onAnnulla }: { c: Contract; busy: boolean; onEsegui: (ref: string | undefined) => Promise<void>; onAnnulla: () => void }) {
  const [ref, setRef] = useState('')
  return (
    <Card title={t('Duplica il contratto')}>
      <p className="muted" style={{ marginTop: 0 }}>{t('Una bozza con le stesse condizioni, voci e documenti, senza beni: per il contratto gemello con un altro fornitore o un altro reparto.')}</p>
      <label className="field" style={{ maxWidth: 360 }}><span className="field__label">{t('Riferimento')}</span><input value={ref} onChange={(e) => setRef(e.target.value)} className="mono" placeholder={`${c.reference}-copia`} /></label>
      <div className="eagle-form__azioni">
        <button type="button" disabled={busy} onClick={() => void onEsegui(ref.trim() || undefined)}>{busy ? t('Salvo…') : t('Duplica')}</button>
        <button type="button" className="secondary" onClick={onAnnulla} disabled={busy}>{t('Annulla')}</button>
      </div>
    </Card>
  )
}

/** I beni coperti, con le date di copertura modificabili, e l'aggiunta dall'inventario dei beni. */
function Copertura({ contratto, coperti, caricamento, puoModificare, busy, esegui }: {
  contratto: Contract; coperti: CoveredAsset[]; caricamento: boolean; puoModificare: boolean; busy: boolean
  esegui: (fn: () => Promise<unknown>) => Promise<boolean>
}) {
  const [aggiungo, setAggiungo] = useState(false)
  const [cerca, setCerca] = useState('')
  const [scelti, setScelti] = useState<string[]>([])
  const [da, setDa] = useState('')
  const [a, setA] = useState('')
  const candidati = useAsync<Asset[]>(() => aggiungo ? api.assets({ q: cerca || undefined, limit: 100 }) : Promise.resolve([]), [aggiungo, cerca])
  const gia = new Set(coperti.map((x) => x.asset_id))
  const [modificaDate, setModificaDate] = useState<{ id: string; from: string; until: string } | null>(null)

  const colonneCandidati: Column<Asset>[] = [
    { key: 'asset_tag', header: t('Cartellino'), mono: true },
    { key: 'name', header: t('Bene') },
    { key: 'category', header: t('Categoria') },
    { key: 'status', header: t('Stato') },
  ]

  const inModifica = (x: CoveredAsset) => modificaDate?.id === x.asset_id
  const colonneCoperti: Column<CoveredAsset>[] = [
    {
      key: 'asset_tag',
      header: t('Cartellino'),
      mono: true,
      value: (x) => [x.asset_tag, x.serial].filter(Boolean).join(' '),
      render: (x) => (
        <>
          <Link to={`/assets/${x.asset_id}`}>{x.asset_tag}</Link>
          {x.serial && <div className="text-muted eagle-piccolo">{x.serial}</div>}
        </>
      ),
    },
    {
      key: 'name',
      header: t('Bene'),
      value: (x) => [x.name, x.category].filter(Boolean).join(' '),
      render: (x) => (
        <>
          {x.name ?? '—'}
          {x.category && <div className="text-muted eagle-piccolo">{x.category}</div>}
        </>
      ),
    },
    {
      key: 'location_name',
      header: t('Sede'),
      render: (x) => x.location_name ?? <span className="text-muted">—</span>,
    },
    { key: 'status', header: t('Stato') },
    {
      key: 'covered_from',
      header: t('Coperto da'),
      type: 'date',
      value: (x) => x.covered_from,
      render: (x) =>
        modificaDate && inModifica(x) ? (
          <input type="date" value={modificaDate.from} onChange={(e) => setModificaDate({ ...modificaDate, from: e.target.value })} aria-label={t('Coperto da')} />
        ) : (
          x.covered_from ?? <span className="text-muted">{t('dall’inizio')}</span>
        ),
    },
    {
      key: 'covered_until',
      header: t('Coperto fino a'),
      type: 'date',
      value: (x) => x.covered_until,
      render: (x) =>
        modificaDate && inModifica(x) ? (
          <input type="date" value={modificaDate.until} onChange={(e) => setModificaDate({ ...modificaDate, until: e.target.value })} aria-label={t('Coperto fino a')} />
        ) : (
          x.covered_until ?? <span className="text-muted">{t('fino alla scadenza')}</span>
        ),
    },
    {
      key: 'valid_today',
      header: t('Valido oggi'),
      value: (x) => (x.valid_today ? t('valido') : t('non valido')),
      render: (x) =>
        x.valid_today ? <span className="badge badge--ok">{t('valido')}</span> : <span className="badge badge--warn">{t('non valido')}</span>,
    },
    ...(puoModificare
      ? [
          {
            key: 'azioni',
            header: '',
            sort: false,
            searchable: false,
            render: (x: CoveredAsset) => (
              <span style={{ display: 'flex', gap: 6 }}>
                {modificaDate && inModifica(x) ? (
                  <>
                    <button type="button" disabled={busy}
                            onClick={() => void esegui(() => api.updateCoverage(contratto.id, x.asset_id, { covered_from: modificaDate.from || null, covered_until: modificaDate.until || null })).then((ok) => { if (ok) setModificaDate(null) })}>{t('Salva')}</button>
                    <button type="button" className="secondary" onClick={() => setModificaDate(null)} disabled={busy}>{t('Annulla')}</button>
                  </>
                ) : (
                  <>
                    <button type="button" className="secondary" disabled={busy} onClick={() => setModificaDate({ id: x.asset_id, from: x.covered_from ?? '', until: x.covered_until ?? '' })}>{t('Date')}</button>
                    {/* Sganciare cancella il collegamento e la sua storia: per un bene
                        uscito a una data, meglio «Date». Il pulsante resta per gli agganci sbagliati. */}
                    <button type="button" className="secondary" disabled={busy}
                            onClick={() => { if (window.confirm(t('Sganciare «{tag}»? Per un bene uscito a una certa data è meglio impostare la fine della copertura.', { tag: x.asset_tag }))) void esegui(() => api.uncoverAsset(contratto.id, x.asset_id)) }}>{t('Sgancia')}</button>
                  </>
                )}
              </span>
            ),
          },
        ]
      : []),
  ]

  return (
    <Card title={t('Beni coperti')} actions={puoModificare && !aggiungo ? <button type="button" onClick={() => setAggiungo(true)}>{t('Aggiungi beni')}</button> : undefined}>
      {aggiungo && (
        <div className="eagle-form contratto-form" style={{ marginBottom: 16 }}>
          <div className="eagle-report__riga">
            <label className="field"><span className="field__label">{t('Cerca nei beni')}</span>
              <input value={cerca} onChange={(e) => { setCerca(e.target.value); setScelti([]) }} placeholder={t('cartellino, nome, matricola')} /></label>
            <label className="field" style={{ maxWidth: 190 }}><span className="field__label">{t('Coperto da')}</span><input type="date" value={da} onChange={(e) => setDa(e.target.value)} /></label>
            <label className="field" style={{ maxWidth: 190 }}><span className="field__label">{t('Coperto fino a')}</span><input type="date" value={a} onChange={(e) => setA(e.target.value)} /></label>
          </div>
          {candidati.loading && !candidati.data ? <Spinner /> : (
            <DataTable
              rows={(candidati.data ?? []).filter((x) => !gia.has(x.id))}
              columns={colonneCandidati}
              rowKey={(x) => x.id}
              conteggio={{ nome: t('asset') }}
              selezione={{ chiavi: new Set(scelti), onCambia: (k) => setScelti([...k]), id: (x) => x.id }}
            />
          )}
          <div className="eagle-form__azioni">
            <button type="button" disabled={busy || scelti.length === 0}
                    onClick={() => void esegui(() => api.coverAssets(contratto.id, scelti, da || undefined, a || undefined)).then((ok) => { if (ok) { setScelti([]); setAggiungo(false) } })}>
              {t('Copri {n} beni', { n: scelti.length })}
            </button>
            <button type="button" className="secondary" onClick={() => setAggiungo(false)} disabled={busy}>{t('Chiudi')}</button>
          </div>
        </div>
      )}

      {caricamento ? <Spinner /> : coperti.length === 0 ? (
        <EmptyState title={t('Nessun bene coperto')} hint={t('Un contratto senza beni non protegge nulla: aggiungili da qui o dalla scheda del bene.')} />
      ) : (
        <DataTable
          rows={coperti}
          columns={colonneCoperti}
          rowKey={(x) => x.asset_id}
          conteggio={{ nome: t('asset') }}
        />
      )}
    </Card>
  )
}

/** Le righe del contratto e il confronto col valore dichiarato. */
function Voci({ c, puoModificare, onSalva }: { c: Contract; puoModificare: boolean; onSalva: (items: ContractItem[]) => Promise<void> }) {
  const [righe, setRighe] = useState<ContractItem[]>(c.items)
  const [modifica, setModifica] = useState(false)
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const totale = righe.reduce((s, r) => s + (r.quantity ?? 1) * (r.unit_price ?? 0), 0)
  const set = (i: number, patch: Partial<ContractItem>) => setRighe(righe.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  const scarto = c.value !== null && righe.length > 0 ? totale - c.value : null

  const importo = (r: ContractItem) => (r.quantity ?? 1) * (r.unit_price ?? 0)
  const colonneVoci: Column<ContractItem>[] = [
    { key: 'description', header: t('Descrizione') },
    { key: 'quantity', header: t('Quantità'), type: 'number', value: (r) => r.quantity },
    {
      key: 'unit_price',
      header: t('Prezzo unitario'),
      type: 'number',
      value: (r) => r.unit_price,
      render: (r) => formattaImporto(r.unit_price, c.currency),
    },
    { key: 'period', header: t('Periodo') },
    {
      key: 'importo',
      header: t('Importo'),
      type: 'number',
      value: importo,
      render: (r) => formattaImporto(importo(r), c.currency),
    },
  ]

  return (
    <Card title={t('Voci e costi')} actions={puoModificare && !modifica ? <button type="button" onClick={() => setModifica(true)}>{t('Modifica le voci')}</button> : undefined}>
      <ErrorBanner error={errore} />
      <div className="stats">
        <div className="stat"><div className="stat__value">{formattaImporto(c.value, c.currency)}</div><div className="stat__label">{t('Valore dichiarato')}</div><div className="stat__hint">{etichettaPeriodicita(c.billing_period)}</div></div>
        <div className="stat"><div className="stat__value">{formattaImporto(totale, c.currency)}</div><div className="stat__label">{t('Totale delle voci')}</div>
          {scarto !== null && Math.abs(scarto) > 0.005 && <div className="stat__hint" style={{ color: 'var(--warn)' }}>{t('scarto {v} rispetto al valore dichiarato', { v: formattaImporto(scarto, c.currency) })}</div>}</div>
        <div className="stat"><div className="stat__value">{formattaImporto(c.annual_value, c.currency)}</div><div className="stat__label">{t('Valore annuo')}</div></div>
        {c.payment_terms && <div className="stat"><div className="stat__value" style={{ fontSize: 16 }}>{c.payment_terms}</div><div className="stat__label">{t('Termini di pagamento')}</div></div>}
      </div>
      {righe.length === 0 && !modifica ? (
        <EmptyState title={t('Nessuna voce')} hint={t('Le righe del contratto (canoni, giornate, licenze per utente): il totale senza le righe non si verifica contro la fattura.')} />
      ) : !modifica ? (
        <DataTable
          rows={righe}
          columns={colonneVoci}
          rowKey={(_r, i) => String(i)}
          conteggio={{ nome: t('voci') }}
        />
      ) : (
        // While editing, the rows are forms in the user's order: a plain grid.
        <Scorrevole>
          <table>
            <thead><tr><th>{t('Descrizione')}</th><th>{t('Quantità')}</th><th>{t('Prezzo unitario')}</th><th>{t('Periodo')}</th><th style={{ textAlign: 'right' }}>{t('Importo')}</th>{modifica && <th />}</tr></thead>
            <tbody>
              {righe.map((r, i) => (
                <tr key={i}>
                  {modifica ? (
                    <>
                      <td><input value={r.description} onChange={(e) => set(i, { description: e.target.value })} aria-label={t('Descrizione')} /></td>
                      <td><input type="number" step="0.01" min="0" value={r.quantity ?? ''} onChange={(e) => set(i, { quantity: e.target.value ? Number(e.target.value) : null })} aria-label={t('Quantità')} style={{ width: 90 }} /></td>
                      <td><input type="number" step="0.01" min="0" value={r.unit_price ?? ''} onChange={(e) => set(i, { unit_price: e.target.value ? Number(e.target.value) : null })} aria-label={t('Prezzo unitario')} style={{ width: 120 }} /></td>
                      <td><input value={r.period ?? ''} onChange={(e) => set(i, { period: e.target.value || null })} aria-label={t('Periodo')} placeholder={t('anno, mese, una tantum')} style={{ width: 120 }} /></td>
                      <td style={{ textAlign: 'right' }}>{formattaImporto((r.quantity ?? 1) * (r.unit_price ?? 0), c.currency)}</td>
                      <td><button type="button" className="linklike" onClick={() => setRighe(righe.filter((_, j) => j !== i))}>{t('togli')}</button></td>
                    </>
                  ) : (
                    <>
                      <td>{r.description}</td><td>{r.quantity ?? '—'}</td><td>{formattaImporto(r.unit_price, c.currency)}</td><td>{r.period ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}>{formattaImporto((r.quantity ?? 1) * (r.unit_price ?? 0), c.currency)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Scorrevole>
      )}
      {modifica && (
        <div className="eagle-form__azioni" style={{ marginTop: 12 }}>
          <button type="button" className="secondary" onClick={() => setRighe([...righe, { description: '', quantity: 1, unit_price: null, period: null }])}>{t('Aggiungi una voce')}</button>
          <button type="button" disabled={busy || righe.some((r) => !r.description.trim())}
                  onClick={async () => { setBusy(true); setErrore(null); try { await onSalva(righe); setModifica(false) } catch (e) { setErrore(e) } finally { setBusy(false) } }}>{busy ? t('Salvo…') : t('Salva')}</button>
          <button type="button" className="secondary" onClick={() => { setRighe(c.items); setModifica(false) }} disabled={busy}>{t('Annulla')}</button>
        </div>
      )}
    </Card>
  )
}

/** Collegamenti ai documenti: il contratto firmato sta nel sistema documentale, non qui. */
function Documenti({ c, puoModificare, onSalva }: { c: Contract; puoModificare: boolean; onSalva: (documents: ContractDocument[]) => Promise<void> }) {
  const [titolo, setTitolo] = useState('')
  const [url, setUrl] = useState('')
  const [kind, setKind] = useState('contract')
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const tipoDoc = (k: string) => {
    switch (k) {
      case 'contract': return t('Contratto firmato')
      case 'offer': return t('Offerta')
      case 'order': return t('Ordine')
      case 'invoice': return t('Fattura')
      case 'sla': return t('Allegato SLA')
      default: return t('Altro')
    }
  }
  async function salva(docs: ContractDocument[]) {
    setBusy(true)
    setErrore(null)
    try {
      await onSalva(docs)
      setTitolo('')
      setUrl('')
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Card title={t('Documenti')}>
      <ErrorBanner error={errore} />
      <p className="muted" style={{ marginTop: 0 }}>{t('Collegamenti, non file: il contratto firmato sta nel sistema documentale dell’organizzazione, e copiarlo qui farebbe due versioni.')}</p>
      {c.documents.length === 0 ? <EmptyState title={t('Nessun documento collegato')} /> : (
        <ul className="contratto-documenti">
          {c.documents.map((d, i) => (
            <li key={i}>
              <span className="badge badge--muted">{tipoDoc(d.kind)}</span>{' '}
              <a href={d.url} target="_blank" rel="noreferrer">{d.title}</a>
              <span className="text-muted eagle-piccolo"> · {d.url}</span>
              {puoModificare && <> <button type="button" className="linklike" disabled={busy} onClick={() => void salva(c.documents.filter((_, j) => j !== i))}>{t('togli')}</button></>}
            </li>
          ))}
        </ul>
      )}
      {puoModificare && (
        <div className="eagle-report__riga" style={{ marginTop: 12, alignItems: 'flex-end' }}>
          <label className="field" style={{ maxWidth: 200 }}><span className="field__label">{t('Tipo')}</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              {['contract', 'offer', 'order', 'invoice', 'sla', 'other'].map((k) => <option key={k} value={k}>{tipoDoc(k)}</option>)}
            </select></label>
          <label className="field"><span className="field__label">{t('Titolo')}</span><input value={titolo} onChange={(e) => setTitolo(e.target.value)} /></label>
          <label className="field" style={{ flex: 2 }}><span className="field__label">{t('Collegamento')}</span><input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://… · \\\\server\\cartella\\file.pdf" className="mono" /></label>
          <button type="button" disabled={busy || !url.trim()} onClick={() => void salva([...c.documents, { title: titolo.trim() || url.trim(), url: url.trim(), kind }])}>{t('Aggiungi')}</button>
        </div>
      )}
    </Card>
  )
}

function Storico({ eventi, caricamento, puoModificare, onNota }: { eventi: ContractEvent[]; caricamento: boolean; puoModificare: boolean; onNota: (testo: string) => Promise<void> }) {
  const [nota, setNota] = useState('')
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  return (
    <Card title={t('Storico')}>
      <ErrorBanner error={errore} />
      {puoModificare && (
        <div className="eagle-report__riga" style={{ alignItems: 'flex-end', marginBottom: 14 }}>
          <label className="field" style={{ flex: 1 }}><span className="field__label">{t('Nuova nota')}</span>
            <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder={t('cosa ha detto il fornitore, cosa si è deciso…')} /></label>
          <button type="button" disabled={busy || !nota.trim()}
                  onClick={async () => { setBusy(true); setErrore(null); try { await onNota(nota.trim()); setNota('') } catch (e) { setErrore(e) } finally { setBusy(false) } }}>{t('Aggiungi')}</button>
        </div>
      )}
      {caricamento ? <Spinner /> : eventi.length === 0 ? <EmptyState title={t('Nessun evento')} /> : (
        <ul className="contratto-storico">
          {eventi.map((e) => (
            <li key={e.id}>
              <div><strong>{etichettaEventoContratto(e.kind)}</strong> <span className="text-muted eagle-piccolo">{formatDateTime(e.at)}{e.actor && ` · ${e.actor}`}</span></div>
              <Evento e={e} />
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

function Evento({ e }: { e: ContractEvent }) {
  const p = e.payload as Record<string, unknown>
  if (e.kind === 'note' || e.kind === 'terminated') return <div style={{ whiteSpace: 'pre-wrap' }}>{e.note}{e.kind === 'terminated' && p.at ? <span className="text-muted"> · {String(p.at)}</span> : null}</div>
  if (e.kind === 'updated' && p.fields && typeof p.fields === 'object') {
    const campi = p.fields as Record<string, { from?: unknown; to?: unknown; changed?: boolean }>
    return (
      <ul className="eagle-piccolo" style={{ margin: '4px 0 0', paddingLeft: 18 }}>
        {Object.entries(campi).map(([k, v]) => (
          <li key={k}>{etichettaCampoContratto(k)}: {v.changed ? t('modificato') : <><span className="text-muted">{String(v.from ?? '—')}</span> → {String(v.to ?? '—')}</>}</li>
        ))}
      </ul>
    )
  }
  if (e.kind === 'renewed') {
    if (p.auto) return <div className="eagle-piccolo">{t('Rinnovo tacito: la scadenza è avanzata da {a} a {b}.', { a: String(p.from), b: String(p.to) })}</div>
    return <div className="eagle-piccolo">{t('Nuovo contratto')}: <Link to={`/contracts/${String(p.new_contract_id)}`} className="mono">{String(p.new_reference)}</Link> · {t('{n} beni portati', { n: Number(p.assets ?? 0) })}</div>
  }
  if (e.kind === 'created') {
    if (p.renewed_from_id) return <div className="eagle-piccolo">{t('Rinnovo di')} <Link to={`/contracts/${String(p.renewed_from_id)}`} className="mono">{String(p.renewed_from_reference)}</Link></div>
    if (p.duplicated_from_id) return <div className="eagle-piccolo">{t('Duplicato da')} <Link to={`/contracts/${String(p.duplicated_from_id)}`} className="mono">{String(p.duplicated_from_reference)}</Link></div>
    return null
  }
  if (e.kind === 'reminder') {
    const giorni = Number(p.days_left ?? 0)
    return <div className="eagle-piccolo">{giorni < 0 ? t('scaduto da {n} giorni', { n: -giorni }) : t('{n} giorni alla data che conta', { n: giorni })} · {t('{n} destinatari', { n: Number(p.recipients ?? 0) })}</div>
  }
  if (e.kind === 'coverage') {
    if (p.added) return <div className="eagle-piccolo">{t('{n} beni aggiunti', { n: Number(p.added) })}</div>
    if (p.removed) return <div className="eagle-piccolo">{t('un bene sganciato')}</div>
    return <div className="eagle-piccolo">{t('date di copertura aggiornate')}</div>
  }
  return null
}
